import { useEffect, useState, type FormEvent } from 'react';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import { Box, Flex, Text, Spinner } from '@chakra-ui/react';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';

import { applyActionTenantId, verifyPasswordResetCode, confirmPasswordReset } from '@/lib/firebase';
import { LogoSvg } from '@/components/svg/LogoSvg';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * Identity Platform のメールアクションリンク（/auth/action）のクエリパラメータ。
 * 標準のパスワードリセット / 招待（初回パスワード設定）メールから遷移してくる。
 *
 * マルチテナント環境では tenantId がリンクに含まれるため、oobCode の検証・確定の前に
 * auth.tenantId へ適用する必要がある（firebaseapp.com の標準ハンドラはこれを行わない）。
 */
const actionSearchSchema = z.object({
  mode: z.string().optional().catch(undefined),
  oobCode: z.string().optional().catch(undefined),
  apiKey: z.string().optional().catch(undefined),
  tenantId: z.string().optional().catch(undefined),
  continueUrl: z.string().optional().catch(undefined),
  lang: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/auth/action')({
  validateSearch: zodValidator(actionSearchSchema),
  component: AuthActionPage,
});

/** パスワード最小文字数 */
const PASSWORD_MIN_LENGTH = 8;

const EXPIRED_LINK_MESSAGE =
  'このリンクは有効期限が切れているか、既に使用されています。管理者に再送を依頼してください。';

type ActionStatus = 'verifying' | 'ready' | 'submitting' | 'success' | 'error';

function AuthActionPage() {
  const navigate = useNavigate();
  const { mode, oobCode, tenantId } = useSearch({ from: '/auth/action' });

  // 対応モードはパスワード設定（リセット / 招待）のみ。無効なリンクは初期状態で確定する
  const isPasswordReset = mode === 'resetPassword' && !!oobCode;

  const [status, setStatus] = useState<ActionStatus>(isPasswordReset ? 'verifying' : 'error');
  const [email, setEmail] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState(
    isPasswordReset ? '' : 'このリンクは無効か、対応していない操作です。'
  );
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [formError, setFormError] = useState<string | undefined>(undefined);

  // マウント時に oobCode を検証する（テナントを適用してから）
  useEffect(() => {
    if (!isPasswordReset || !oobCode) return;

    let cancelled = false;

    // マルチテナント: リンクの tenantId を Auth に適用してから検証する
    applyActionTenantId(tenantId);

    verifyPasswordResetCode(oobCode)
      .then((verifiedEmail) => {
        if (cancelled) return;
        setEmail(verifiedEmail);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessage(EXPIRED_LINK_MESSAGE);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [isPasswordReset, oobCode, tenantId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);

    if (password.length < PASSWORD_MIN_LENGTH) {
      setFormError(`パスワードは ${PASSWORD_MIN_LENGTH} 文字以上で入力してください。`);
      return;
    }
    if (password !== passwordConfirm) {
      setFormError('パスワードが一致しません。');
      return;
    }
    if (!oobCode) return;

    setStatus('submitting');
    try {
      // テナントを再適用してからパスワードを確定する（リロード対策）
      applyActionTenantId(tenantId);
      await confirmPasswordReset(oobCode, password);
      setStatus('success');
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
        setErrorMessage(EXPIRED_LINK_MESSAGE);
        setStatus('error');
        return;
      }
      if (code === 'auth/weak-password') {
        setFormError('パスワードが脆弱です。より複雑なパスワードを設定してください。');
      } else {
        setFormError('パスワードの設定に失敗しました。時間をおいて再度お試しください。');
      }
      setStatus('ready');
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="bg.DEFAULT">
      <Box w="full" maxW="400px" bg="bg.panel" p={8} borderRadius="lg" shadow="lg">
        <Flex align="center" justify="center" gap={2} mb={6}>
          <LogoSvg style={{ width: '96px', height: '17px' }} />
          <Text fontSize="lg" fontWeight="bold">
            管理コンソール
          </Text>
        </Flex>

        {status === 'verifying' && (
          <Flex direction="column" align="center" gap={3} py={4}>
            <Spinner />
            <Text fontSize="sm" color="fg.muted">
              リンクを確認しています...
            </Text>
          </Flex>
        )}

        {status === 'error' && (
          <Flex direction="column" gap={4}>
            <Box bg="admin.error.bg" color="fg.error" p={3} borderRadius="md" fontSize="sm">
              {errorMessage}
            </Box>
            <Button variant="outline" w="full" onClick={() => navigate({ to: '/login' })}>
              ログイン画面へ
            </Button>
          </Flex>
        )}

        {status === 'success' && (
          <Flex direction="column" gap={4}>
            <Text fontSize="lg" fontWeight="bold">
              パスワードを設定しました
            </Text>
            <Text fontSize="sm" color="fg.muted">
              新しいパスワードでログインできます。
            </Text>
            <Button colorPalette="brand" w="full" onClick={() => navigate({ to: '/login' })}>
              ログイン画面へ
            </Button>
          </Flex>
        )}

        {(status === 'ready' || status === 'submitting') && (
          <>
            <Text fontSize="lg" fontWeight="bold" mb={1}>
              パスワードの設定
            </Text>
            {email && (
              <Text fontSize="sm" color="fg.muted" mb={4}>
                {email}
              </Text>
            )}

            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap={4}>
                {formError && (
                  <Box bg="admin.error.bg" color="fg.error" p={3} borderRadius="md" fontSize="sm">
                    {formError}
                  </Box>
                )}

                <Box>
                  <Text as="label" fontSize="sm" fontWeight="medium" mb={1}>
                    新しいパスワード
                  </Text>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={`${PASSWORD_MIN_LENGTH} 文字以上`}
                    required
                    disabled={status === 'submitting'}
                  />
                </Box>

                <Box>
                  <Text as="label" fontSize="sm" fontWeight="medium" mb={1}>
                    新しいパスワード（確認）
                  </Text>
                  <Input
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="もう一度入力"
                    required
                    disabled={status === 'submitting'}
                  />
                </Box>

                <Button
                  type="submit"
                  colorPalette="brand"
                  w="full"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? (
                    <Flex align="center" justify="center" gap={2}>
                      <Spinner size="sm" />
                      設定中...
                    </Flex>
                  ) : (
                    'パスワードを設定'
                  )}
                </Button>
              </Flex>
            </form>
          </>
        )}
      </Box>
    </Flex>
  );
}
